<template>
  <v-container fluid class="list-page term-page">
    <Create
      :createDialog="createDialog"
      :selectedYear="selectedYear"
      @close="closeCreate"
      @submit="getList"
    />
    <Edit
      :editDialog="editDialog"
      :editTerm="selectedTerm"
      @close="editDialog = false"
      @submit="getList"
    />

    <div class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Report cards</div>
        <h1>Terms</h1>
        <p>
          Organize academic terms by year and manage their reporting periods.
        </p>
      </div>
      <div class="list-page-heading__actions">
        <v-btn
          v-if="checkRightStatus(37)"
          color="primary"
          depressed
          @click="add"
        >
          <v-icon left>mdi-plus</v-icon>New term
        </v-btn>
      </div>
    </div>

    <v-row class="list-metrics">
      <v-col cols="6" sm="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21"
              >mdi-calendar-range-outline</v-icon
            >
          </div>
          <div>
            <div class="list-metric__value">{{ groupedYears.length }}</div>
            <div class="list-metric__label">Academic years</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-calendar-multiple</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ filteredTerms.length }}</div>
            <div class="list-metric__label">Terms in result</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21"
              >mdi-calendar-check-outline</v-icon
            >
          </div>
          <div>
            <div class="list-metric__value">{{ currentTermCount }}</div>
            <div class="list-metric__label">Current terms</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21"
              >mdi-calendar-clock-outline</v-icon
            >
          </div>
          <div>
            <div class="list-metric__value">{{ upcomingTermCount }}</div>
            <div class="list-metric__label">Upcoming terms</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card :loading="loading" elevation="0" class="list-card term-list-card">
      <div class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search by term, academic year, or department"
          single-line
          hide-details
          clearable
        />
        <v-select
          v-model="selectedStartYear"
          :items="yearStartYearOptions"
          class="list-toolbar__control"
          outlined
          dense
          background-color="#F8FAF8"
          prepend-inner-icon="mdi-calendar-start"
          label="Year start year"
          hide-details
        />

        <div class="list-toolbar__actions">
          <v-btn
            icon
            outlined
            color="primary"
            :loading="loading"
            aria-label="Refresh terms"
            @click="getList"
          >
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="list-groups">
        <v-expansion-panels
          v-if="groupedYears.length"
          v-model="openedPanels"
          multiple
          flat
          class="list-group-panels"
        >
          <v-expansion-panel
            v-for="yearGroup in groupedYears"
            :key="yearGroup.id"
          >
            <v-expansion-panel-header class="list-group-header">
              <div class="list-group-heading">
                <div class="list-group-heading__icon">
                  <v-icon color="primary" size="20">mdi-calendar-range</v-icon>
                </div>
                <div class="list-group-heading__copy">
                  <strong>{{ yearGroup.name }}</strong>
                  <span>{{ yearGroup.className || "All departments" }}</span>
                </div>
                <div class="list-group-heading__actions">
                  <v-chip
                    small
                    label
                    color="green lighten-5"
                    text-color="green darken-2"
                  >
                    {{ yearGroup.terms.length }}
                    {{ yearGroup.terms.length === 1 ? "Term" : "Terms" }}
                  </v-chip>
                  <v-btn
                    v-if="checkRightStatus(37)"
                    small
                    outlined
                    color="primary"
                    @click.stop="addTermToYear(yearGroup)"
                  >
                    <v-icon left small>mdi-plus</v-icon>Add term
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <div v-if="yearGroup.terms.length">
                <v-simple-table class="list-nested-table term-table">
                  <thead>
                    <tr>
                      <th class="text-left">Term</th>
                      <th class="text-left">Reporting period</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Created</th>
                      <th class="text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="term in yearGroup.terms" :key="term.id">
                      <td>
                        <div class="term-name">
                          <span class="term-marker"></span
                          ><strong>{{ term.name }}</strong>
                        </div>
                      </td>
                      <td>
                        <div class="term-period">
                          <strong>{{ formatDate(term.startDate) }}</strong
                          ><v-icon size="15">mdi-arrow-right</v-icon
                          ><strong>{{ formatDate(term.endDate) }}</strong>
                        </div>
                        <span class="term-duration">{{
                          termDuration(term)
                        }}</span>
                      </td>
                      <td>
                        <v-chip
                          small
                          label
                          :class="termStatus(term).className"
                          >{{ termStatus(term).label }}</v-chip
                        >
                        <span class="term-completion-date">{{
                          completionCopy(term)
                        }}</span>
                      </td>
                      <td>
                        <div class="list-cell-primary">
                          {{ term.createdAt | moment("MMM D, YYYY") }}
                        </div>
                        <div class="list-cell-secondary">
                          {{ term.createdAt | moment("from", "now") }}
                        </div>
                      </td>
                      <td class="text-right">
                        <v-menu bottom left offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon>
                              <v-icon>mdi-dots-horizontal</v-icon>
                            </v-btn>
                          </template>

                          <v-list class="menu-button">
                            <v-list-item
                              v-if="
                                term.completionStatus === 'completed' &&
                                checkRightStatus(39)
                              "
                              :disabled="unlockingTermId === term.id"
                              @click="unlockTerm(term)"
                            >
                              <v-list-item-icon
                                ><v-icon color="primary"
                                  >mdi-lock-open-variant-outline</v-icon
                                ></v-list-item-icon
                              >
                              <v-list-item-content>
                                <v-list-item-title
                                  >Temporarily unlock</v-list-item-title
                                >
                                <v-list-item-subtitle
                                  >Allow grade changes
                                  again</v-list-item-subtitle
                                >
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item
                              v-if="checkRightStatus(39)"
                              @click="editTerm(term)"
                            >
                              <v-list-item-title>Edit</v-list-item-title>
                            </v-list-item>

                            <v-list-item
                              v-if="checkRightStatus(40)"
                              @click="deleteTerm(term)"
                            >
                              <v-list-item-title>Delete</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <div v-else class="list-empty-inline">
                <div class="list-empty-inline__message">
                  <v-icon color="grey lighten-1"
                    >mdi-calendar-blank-outline</v-icon
                  ><span>No terms have been added to this academic year.</span>
                </div>
                <v-btn
                  v-if="checkRightStatus(37)"
                  small
                  outlined
                  color="primary"
                  @click="addTermToYear(yearGroup)"
                >
                  <v-icon left small>mdi-plus</v-icon>Add first term
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-else class="list-empty-state py-12">
          <v-icon size="48" color="grey lighten-1"
            >mdi-calendar-search-outline</v-icon
          >
          <strong>No terms found</strong>
          <span>{{ emptyStateText }}</span>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import termService from "../service";
import yearService from "@/modules/ReportCard/Year/service.js";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "term-list",
  components: {
    Create,
    Edit,
  },
  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedTerm: null,
      selectedYear: null,
      terms: [],
      academicYears: [],
      search: null,
      selectedStartYear: new Date().getFullYear(),
      loading: true,
      openedPanels: [],
      unlockingTermId: null,
    };
  },
  created() {
    this.getList();
    document.title = "taleem - Term List";
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    filteredAcademicYears() {
      const selectedStartYear = String(this.selectedStartYear);
      const yearsForStartYear = this.academicYears.filter(
        (year) => String(this.academicYearStartValue(year)) === selectedStartYear
      );
      if (!this.search) return yearsForStartYear;

      const search = this.search.toLowerCase();
      return yearsForStartYear.filter((year) => {
        const yearName = year.name ? year.name.toLowerCase() : "";
        const className = year.Class && year.Class.name ? year.Class.name.toLowerCase() : "";
        const termMatches = this.terms.some(
          (term) =>
            Number(term.YearId || (term.Year && term.Year.id)) === Number(year.id) &&
            String(term.name || "").toLowerCase().includes(search)
        );
        return yearName.includes(search) || className.includes(search) || termMatches;
      });
    },

    filteredTerms() {
      const visibleYearIds = new Set(this.filteredAcademicYears.map((year) => Number(year.id)));
      return this.terms.filter((term) => visibleYearIds.has(Number(term.YearId || (term.Year && term.Year.id))));
    },

    yearStartYearOptions() {
      const years = new Set([new Date().getFullYear()]);
      this.academicYears.forEach((year) => years.add(this.academicYearStartValue(year)));
      return Array.from(years)
        .sort((left, right) => {
          if (left === "undated") return 1;
          if (right === "undated") return -1;
          return Number(right) - Number(left);
        })
        .map((year) => ({ text: year === "undated" ? "Dates not set" : String(year), value: year }));
    },

    emptyStateText() {
      if (this.search)
        return "Try a different term, academic year, or department.";
      if (this.selectedStartYear === "undated") return "No academic years without dates were found.";
      return `No academic years start in ${this.selectedStartYear}. Select another year or create an academic year for this period.`;
    },

    groupedYears() {
      const groupedMap = {};

      this.filteredAcademicYears.forEach((year) => {
        groupedMap[year.id] = {
          id: year.id,
          name: year.name || "Unnamed academic year",
          className: year.Class && year.Class.name ? year.Class.name : "",
          year,
          terms: [],
        };
      });

      this.filteredTerms.forEach((term) => {
        const yearId = term.YearId || (term.Year && term.Year.id);
        if (groupedMap[yearId]) groupedMap[yearId].terms.push(term);
      });

      return Object.values(groupedMap)
        .map((group) => {
          group.terms = group.terms.sort((a, b) => {
            const nameA = a.name || "";
            const nameB = b.name || "";
            return nameA.localeCompare(nameB);
          });
          return group;
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    currentTermCount() {
      return this.filteredTerms.filter(
        (term) => this.termStatus(term).key === "current"
      ).length;
    },

    upcomingTermCount() {
      return this.filteredTerms.filter(
        (term) => this.termStatus(term).key === "upcoming"
      ).length;
    },
  },
  watch: {
    search() {
      this.expandAllWhenSearching();
    },
    selectedStartYear() {
      this.expandAllWhenSearching();
    },
  },
  methods: {
    academicYearStartValue(year) {
      if (year && year.startDate && this.$moment(year.startDate).isValid()) {
        return this.$moment(year.startDate).year();
      }
      const legacyYear = String((year && year.name) || "").match(/\b(?:19|20)\d{2}\b/);
      return legacyYear ? Number(legacyYear[0]) : "undated";
    },

    add() {
      this.selectedYear = null;
      this.createDialog = true;
    },

    addTermToYear(yearGroup) {
      this.selectedYear = yearGroup.year;
      this.createDialog = true;
    },

    closeCreate() {
      this.createDialog = false;
      this.selectedYear = null;
    },

    expandAllWhenSearching() {
      if (this.search && this.groupedYears.length) {
        this.openedPanels = this.groupedYears.map((_, index) => index);
      } else {
        this.openedPanels = [];
      }
    },

    formatDate(date) {
      return date ? this.$moment(date).format("MMM D, YYYY") : "Not set";
    },

    termDuration(term) {
      if (!term.startDate || !term.endDate) return "Dates need attention";
      const days =
        this.$moment(term.endDate).diff(this.$moment(term.startDate), "days") +
        1;
      return days > 0 ? `${days} days` : "Check date range";
    },

    termStatus(term) {
      if (!term.startDate || !term.endDate) {
        return {
          key: "incomplete",
          label: "Dates missing",
          className: "term-status term-status--incomplete",
        };
      }
      if (term.completionStatus === "temporarily_unlocked") {
        return {
          key: "temporarily_unlocked",
          label: "Temporarily unlocked",
          className: "term-status term-status--unlocked",
        };
      }
      if (term.completionStatus === "completed") {
        return {
          key: "completed",
          label: "Completed",
          className: "term-status term-status--completed",
        };
      }
      const today = this.$moment().startOf("day");
      const start = this.$moment(term.startDate).startOf("day");
      const end = this.$moment(term.endDate).endOf("day");
      if (today.isBefore(start)) {
        return {
          key: "upcoming",
          label: "Upcoming",
          className: "term-status term-status--upcoming",
        };
      }
      if (today.isAfter(end)) {
        return {
          key: "closing",
          label: "Closing period",
          className: "term-status term-status--closing",
        };
      }
      return {
        key: "current",
        label: "Current",
        className: "term-status term-status--current",
      };
    },

    completionCopy(term) {
      if (!term.nextCompletionDate) return "Completion date unavailable";
      const date = this.$moment(term.nextCompletionDate).format(
        "MMM D, YYYY [at] h:mm A"
      );
      if (term.completionStatus === "temporarily_unlocked")
        return `Completes again ${date}`;
      if (term.completionStatus === "completed") return `Completed ${date}`;
      return `Completes ${date}`;
    },

    async unlockTerm(term) {
      const result = await this.$swal({
        title: `Unlock ${term.name}?`,
        text: "Teachers will be able to edit grades temporarily. The new completion time will be shown after unlocking.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#43A047",
        confirmButtonText: "Unlock term",
      });
      if (!result.value) return;
      this.unlockingTermId = term.id;
      try {
        const response = await termService.unlock(term.id);
        const updated = response.data || response;
        Object.assign(term, updated);
        this.$swal.fire({
          icon: "success",
          title: "Term temporarily unlocked",
          text: `Teachers can enter grades until ${this.$moment(
            term.nextCompletionDate
          ).format("MMM D, YYYY [at] h:mm A")}.`,
        });
      } catch (error) {
        this.$swal.fire({
          icon: "error",
          title: "Could not unlock term",
          text:
            (error.data && error.data.message) ||
            "Please refresh and try again.",
        });
      } finally {
        this.unlockingTermId = null;
      }
    },

    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;
      this.selectedYear = null;

      try {
        const [termResponse, yearResponse] = await Promise.all([
          termService.getAll(),
          yearService.getAll({ limit: 10000 }),
        ]);
        this.terms = termResponse.data || [];
        this.academicYears = yearResponse.data || [];
        this.expandAllWhenSearching();
        return termResponse;
      } finally {
        this.loading = false;
      }
    },

    editTerm(term) {
      this.selectedTerm = term;
      this.editDialog = true;
    },

    deleteTerm(term) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          termService.delete(term.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Term has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.term-list-card {
  overflow: hidden;
}

.term-table ::v-deep th {
  height: 42px;
  color: #718075 !important;
  background: #f8faf8;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.term-table ::v-deep td {
  height: 68px;
  border-color: #edf0ed !important;
}
.term-table ::v-deep tbody tr:hover {
  background: #fbfdfb !important;
}
.term-name {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #273247;
}
.term-marker {
  width: 4px;
  height: 28px;
  border-radius: 4px;
  background: #43a047;
}
.term-period {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #465349;
  font-size: 12px;
}
.term-duration {
  display: block;
  margin-top: 3px;
  color: #929d95;
  font-size: 10px;
}
.term-completion-date {
  display: block;
  margin-top: 5px;
  color: #718075;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}
.term-status {
  font-weight: 700;
}
.term-status--current {
  color: #24753b !important;
  background: #e8f5e9 !important;
}
.term-status--upcoming {
  color: #42656d !important;
  background: #edf3f4 !important;
}
.term-status--completed {
  color: #68736a !important;
  background: #f0f2f0 !important;
}
.term-status--unlocked {
  color: #2f6c91 !important;
  background: #eaf4fb !important;
}
.term-status--closing {
  color: #8a6525 !important;
  background: #fff5df !important;
}
.term-status--incomplete {
  color: #80641b !important;
  background: #fff8e2 !important;
}
@media (max-width: 700px) {
  .term-table ::v-deep thead {
    display: none;
  }
  .term-table ::v-deep tbody tr {
    display: grid;
    grid-template-columns: 1fr auto;
    padding: 12px 14px;
    border-top: 1px solid #edf0ed;
  }
  .term-table ::v-deep tbody td {
    display: block;
    height: auto;
    padding: 6px 0 !important;
    border: 0 !important;
  }
  .term-table ::v-deep tbody td:first-child,
  .term-table ::v-deep tbody td:nth-child(2),
  .term-table ::v-deep tbody td:nth-child(3),
  .term-table ::v-deep tbody td:nth-child(4) {
    grid-column: 1;
  }
  .term-table ::v-deep tbody td:last-child {
    grid-column: 2;
    grid-row: 1;
  }
}
</style>
